import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import Card from "../../../components/Card";
import DataTable from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import {Role, RolesResponse} from "../../../types/Responses/RolesAndPermissions";
import TD from "../../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import useComponentVisible from "../../../hooks/useComponentVisible";
import Modal from "../../../components/Modal/Modal";
import FooterMenu from "../../../components/Sidebar/FooterMenu";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {useToastContext} from "../../../types/contexts/ToastContext";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../../reducers/sidebar";

const Roles = () => {
    const {t} = useTranslation();
    const user = useUserContext();
    const navigate = useNavigate();
    const handleError = useErrorHandler();
    const toast = useToastContext();
    const dispatch = useDispatch();
    const [roles, setRoles] = useState<RolesResponse>();
    const [assignedRole, setAssignedRole] = useState<string | null>(null);
    const [modalRole, setModalRole] = useState<Role | null>();
    const {ref, isComponentVisible, setIsComponentVisible} = useComponentVisible(false, '#replace_role');
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const defaultRequest = useMemo<DataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<DataTableRequest>(defaultRequest);

    const sendRequest = useCallback((request: DataTableRequest) => {
        setIsDataTableLoading(true);
        API.getRoles(request).then((response: AxiosResponse) => {
            const rolesResponse = response.data as RolesResponse;
            setRoles(rolesResponse);
            setIsDataTableLoading(false);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_ROLES}`, toast, handleError, setIsForbidden));
    }, [handleError, toast]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        persistDataTableRequest(request);
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...request, search: ''} : request);
        }
    }, [persistDataTableRequest, sendRequest]);

    const onAssignedRoleChanged = (name: string | number) => {
        setAssignedRole(name.toString());
    }

    const closeModal = useCallback(() => {
        setIsComponentVisible(false);
        setAssignedRole('');
        setModalRole(null);
    }, [setIsComponentVisible, setAssignedRole, setModalRole]);

    const sendDeleteRequest = useCallback((role: Role) => {
        if (role && roles) {
            setIsDataTableLoading(true);
            const filteredArray = {...roles, data: roles.data.filter(r => r.id !== role.id), count: roles.count - 1};
            API.deleteRole(role.id, assignedRole ?? '').then(() => {
                if (role) {
                    setRoles(filteredArray)
                    setIsDataTableLoading(false);
                }
                closeModal();
            }).catch(e => ErrorHandler(e, `delete${APILink.UPDATE_ROLE.replace('$role', role.id.toString())}`, toast, handleError));
        }
    }, [setIsDataTableLoading, setRoles, roles, assignedRole, closeModal, toast, handleError]);

    const deleteRole = useCallback((role: Role) => {
        setModalRole(role);
        if (role.users_count) {
            setIsComponentVisible(true);
        } else sendDeleteRequest(role);
    }, [setModalRole, setIsComponentVisible, sendDeleteRequest]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);

    useEffect(() => {
        setTitle(t('admin.roles.title'));
        dispatch(setSidebarTitle(t('admin.roles.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendRequest(dataTableRequest);
            }).catch()
        }
    }, [user]);

    const modalUI = useMemo(() => {
        if (!modalRole || !isComponentVisible || !roles)
            return <></>

        return <Modal refer={ref} showModal={isComponentVisible} title={t('admin.roles.replace.title')}>
            <>
                <p>{t('admin.roles.replace.message', {users: modalRole.users_count})}</p>
                <FooterMenu id='#replace_role' selected={''}
                            icon={assignedRole ? <>{assignedRole}</> : <>{t('admin.roles.choose')}</>}
                            onClick={onAssignedRoleChanged}
                            list={roles.data.filter(r => r.id !== modalRole.id).map(r => {
                                return {name: r.name};
                            })}
                            className="mt-2 p-2 rounded-[0.5rem]"
                />
                <Button className="mt-8" color="teal"
                        onClick={() => sendDeleteRequest(modalRole)}>{t('admin.roles.confirm')}</Button>
                <span className="mt-1 cursor-pointer text-gray-400 dark:text-gray-700"
                      onClick={() => closeModal()}>{t('admin.roles.close')}</span>
            </>
        </Modal>
    }, [modalRole, isComponentVisible, assignedRole, sendDeleteRequest, closeModal, ref, roles]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('modify reporting schema') &&
                    <Button onClick={() => navigate('/app/admin/roles/schema')}
                            className="bg-black text-white">{t('admin.roles.reporting_schema.title')}</Button>}

                {user?.user.permissions.includes('create role') &&
                    <Button onClick={() => navigate('/app/admin/roles/create')}
                            className="bg-black text-white">{t('admin.roles.new.button')}</Button>}
            </div>
        )
    }, [user, refresh]);

    const dataTable = useMemo(() => {
        if (!roles)
            return <></>;

        return <DataTable {...dataTableRequest}
                          total_pages={roles.pages.total}
                          count={roles.count}
                          nodata={roles.data.length === 0}
                          inProgress={isDataTableLoading}
                          rightPanel={actionsPanel}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t('admin.roles.table.name'), sortBy: 'name'},
            {name: t('admin.roles.table.users'), className: 'text-center'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {roles.data.map((role, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD>{role.name}</TD>
                        <TD className="text-center">{role.users_count}</TD>
                        <TD className="text-right">
                            <>
                                {user?.user.permissions.includes('update role') &&
                                    <Button onClick={() => navigate(`/app/admin/roles/${role.id}`)}
                                            className="px-5 py-2 text-white bg-black"><FontAwesomeIcon
                                        icon={solid("pen-to-square")}/></Button>}
                                {role.name !== 'Admin' && role.id !== 2 && user?.user.permissions.includes('delete role') &&
                                    <Button onClick={() => deleteRole(role)}
                                            className="lg:ml-2 px-5 py-2 text-red-300 bg-black"><FontAwesomeIcon
                                        icon={solid("trash")}/></Button>}
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, isDataTableLoading, roles, deleteRole, navigate, onDataTableChanged, user?.user.permissions]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {modalUI}
                    {dataTable}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Roles);
